import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import FamilyMemberRoster from './FamilyMemberRoster';
import InviteMemberModal from './InviteMemberModal';
import CreateMemberModal from './CreateMemberModal';

function FamilySettings({ subscription, onSubscriptionChange }) {
  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showDissolve, setShowDissolve] = useState(false);
  const [error, setError] = useState('');

  const isOrganizer = familyData?.members?.some(
    m => m.role === 'organizer' && m.user_id === subscription?.userId
  );

  const fetchFamily = () => {
    setLoading(true);
    api.get('/family')
      .then(r => {
        setFamilyData(r.data);
        setFamilyName(r.data.group?.name || '');
      })
      .catch(err => {
        if (err.response?.status === 404) setFamilyData(null);
        else setError('Failed to load family data');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchFamily(); }, []);

  const handleCreateGroup = async () => {
    try {
      await api.post('/family/create', { name: familyName || null });
      fetchFamily();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create family group');
    }
  };

  const handleSaveName = async () => {
    try {
      await api.put('/family/name', { name: familyName });
      setEditingName(false);
      fetchFamily();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update name');
    }
  };

  const handleRemoveMember = async (userId, username) => {
    if (!window.confirm(`Remove ${username} from the family? They'll have 30 days to set up their own subscription.`)) return;
    try {
      await api.delete(`/family/members/${userId}`);
      fetchFamily();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleDissolve = async () => {
    try {
      await api.delete('/family');
      setShowDissolve(false);
      fetchFamily();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to dissolve group');
    }
  };

  if (loading) return <div className="loading-spinner" />;

  // No family group yet — show create prompt
  if (!familyData) {
    return (
      <div>
        <h3 style={{ marginBottom: '0.5rem' }}>Family Group</h3>
        <p style={{ opacity: 0.8, fontSize: '0.875em', marginBottom: '1rem' }}>
          Create a family group to share memories and manage members under your plan.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
          <input
            value={familyName}
            onChange={e => setFamilyName(e.target.value)}
            placeholder="Family name (e.g. The Smith Family)"
            style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          />
          <button className="btn-save" onClick={handleCreateGroup}>Create Group</button>
        </div>
        {error && <p style={{ color: 'red', fontSize: '0.875em' }}>{error}</p>}
      </div>
    );
  }

  const { group, members = [], pendingInvitations = [] } = familyData;
  const activeMembers = members.filter(m => m.status !== 'removed');
  const includedMembers = subscription?.maxFamilyMembers || 3;
  const extraMembers = Math.max(0, activeMembers.length - includedMembers);

  return (
    <div>
      {/* Family name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {editingName ? (
          <>
            <input
              value={familyName}
              onChange={e => setFamilyName(e.target.value)}
              style={{ flex: 1, padding: '0.4rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '1.1rem', fontWeight: 'bold' }}
            />
            <button className="btn-save" onClick={handleSaveName}>Save</button>
            <button onClick={() => { setEditingName(false); setFamilyName(group.name || ''); }} style={{ border: 'none', background: 'none', cursor: 'pointer', opacity: 0.6 }}>Cancel</button>
          </>
        ) : (
          <>
            <h3 style={{ margin: 0 }}>{group.name || 'My Family'}</h3>
            {isOrganizer && (
              <button onClick={() => setEditingName(true)} style={{ border: 'none', background: 'none', cursor: 'pointer', opacity: 0.6, fontSize: '0.875em' }}>
                Edit name
              </button>
            )}
          </>
        )}
      </div>

      {/* Member roster */}
      <FamilyMemberRoster members={activeMembers} selectedMemberId={null} onSelect={null} showRoleBadge />

      {/* Member count + add buttons */}
      {isOrganizer && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
          <span style={{ fontSize: '0.875em', opacity: 0.7 }}>
            {activeMembers.length} of {includedMembers} included members
            {extraMembers > 0 && ` (+${extraMembers} extra @ $3/mo each)`}
          </span>
          {activeMembers.length < (group.max_members || 10) && (
            <>
              <button onClick={() => setShowInvite(true)} style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '0.35rem 0.75rem', background: 'none', cursor: 'pointer', fontSize: '0.875em' }}>
                + Invite by email
              </button>
              <button onClick={() => setShowCreate(true)} style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '0.35rem 0.75rem', background: 'none', cursor: 'pointer', fontSize: '0.875em' }}>
                + Create account for someone
              </button>
            </>
          )}
        </div>
      )}

      {/* Member list with remove actions */}
      {isOrganizer && (
        <div style={{ marginBottom: '1.5rem' }}>
          {activeMembers.filter(m => m.role !== 'organizer').map(m => (
            <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #eee', fontSize: '0.875em' }}>
              <div>
                <strong>{m.username}</strong>
                {m.email && <span style={{ opacity: 0.6, marginLeft: '0.5rem' }}>{m.email}</span>}
                {m.status === 'grace_period' && (
                  <span style={{ marginLeft: '0.5rem', color: '#c2410c', fontSize: '0.85em' }}>
                    (grace period ends {new Date(m.grace_period_ends_at).toLocaleDateString()})
                  </span>
                )}
              </div>
              {m.status === 'active' && (
                <button
                  onClick={() => handleRemoveMember(m.user_id, m.username)}
                  style={{ border: 'none', background: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '0.85em' }}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pending invitations */}
      {isOrganizer && pendingInvitations.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ fontSize: '0.875em', opacity: 0.7, marginBottom: '0.5rem' }}>Pending Invitations</h4>
          {pendingInvitations.map(inv => (
            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.875em', opacity: 0.8 }}>
              <span>{inv.email}</span>
              <span style={{ fontSize: '0.8em' }}>
                Expires {new Date(inv.expires_at).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {error && <p style={{ color: 'red', fontSize: '0.875em', marginBottom: '0.75rem' }}>{error}</p>}

      {/* Danger zone */}
      {isOrganizer && (
        <div style={{ borderTop: '1px solid #fee2e2', paddingTop: '1rem', marginTop: '0.5rem' }}>
          <h4 style={{ color: '#e53e3e', fontSize: '0.875em', marginBottom: '0.5rem' }}>Danger Zone</h4>
          {showDissolve ? (
            <div style={{ background: '#fef2f2', borderRadius: '6px', padding: '1rem', fontSize: '0.875em' }}>
              <p style={{ marginBottom: '0.75rem' }}>Dissolving the group will give all members a 30-day grace period, then they'll drop to the Free tier. All content remains safe.</p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleDissolve} style={{ background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.4rem 0.75rem', cursor: 'pointer' }}>
                  Yes, dissolve
                </button>
                <button onClick={() => setShowDissolve(false)} style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '0.4rem 0.75rem', background: 'none', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowDissolve(true)}
              style={{ border: '1px solid #fca5a5', color: '#e53e3e', background: 'none', borderRadius: '6px', padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.875em' }}
            >
              Dissolve family group
            </button>
          )}
        </div>
      )}

      {showInvite && (
        <InviteMemberModal
          onClose={() => setShowInvite(false)}
          onInvited={() => { setShowInvite(false); fetchFamily(); }}
        />
      )}
      {showCreate && (
        <CreateMemberModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchFamily(); }}
        />
      )}
    </div>
  );
}

export default FamilySettings;
